import React from 'react';
import { Plane, Ship, ArrowUpRight, ArrowDownLeft } from 'lucide-react';

/**
 * The four lanes, as cards.
 *
 * The dark variants used to be assembled at runtime — `dark:${tab.bgDark}` —
 * which Tailwind never sees in the source and so never generates, leaving the
 * unpicked lanes with no dark mode at all. The shadow did the same through
 * `shadow-${gradient.split('-')[1]}-500/50`. Every class is written out here.
 */
const TABS = [
  {
    id: 'Air-Export',
    label: 'Air',
    sub: 'Export',
    icon: Plane,
    badge: ArrowUpRight,
    on: 'border-blue-700/30 from-blue-500 to-blue-600 shadow-blue-600/25 hover:shadow-blue-600/35',
    off: 'border-slate-200 bg-white hover:border-blue-300 hover:shadow-blue-500/10 '
       + 'dark:border-slate-800 dark:bg-slate-900/60 dark:hover:border-blue-500/40',
    tile: 'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-md shadow-blue-600/25',
    arrow: 'bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300',
    glow: 'bg-blue-500/20',
  },
  {
    id: 'Sea-Export',
    label: 'Sea',
    sub: 'Export',
    icon: Ship,
    badge: ArrowUpRight,
    on: 'border-teal-700/30 from-teal-500 to-teal-600 shadow-teal-600/25 hover:shadow-teal-600/35',
    off: 'border-slate-200 bg-white hover:border-teal-300 hover:shadow-teal-500/10 '
       + 'dark:border-slate-800 dark:bg-slate-900/60 dark:hover:border-teal-500/40',
    tile: 'bg-gradient-to-br from-teal-500 to-teal-600 text-white shadow-md shadow-teal-600/25',
    arrow: 'bg-teal-50 text-teal-600 dark:bg-teal-500/15 dark:text-teal-300',
    glow: 'bg-teal-500/20',
  },
  {
    id: 'Air-Import',
    label: 'Air',
    sub: 'Import',
    icon: Plane,
    badge: ArrowDownLeft,
    on: 'border-violet-700/30 from-violet-500 to-violet-600 shadow-violet-600/25 hover:shadow-violet-600/35',
    off: 'border-slate-200 bg-white hover:border-violet-300 hover:shadow-violet-500/10 '
       + 'dark:border-slate-800 dark:bg-slate-900/60 dark:hover:border-violet-500/40',
    tile: 'bg-gradient-to-br from-violet-500 to-violet-600 text-white shadow-md shadow-violet-600/25',
    arrow: 'bg-violet-50 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300',
    glow: 'bg-violet-500/20',
  },
  {
    id: 'Sea-Import',
    label: 'Sea',
    sub: 'Import',
    icon: Ship,
    badge: ArrowDownLeft,
    on: 'border-indigo-700/30 from-indigo-500 to-indigo-600 shadow-indigo-600/25 hover:shadow-indigo-600/35',
    off: 'border-slate-200 bg-white hover:border-indigo-300 hover:shadow-indigo-500/10 '
       + 'dark:border-slate-800 dark:bg-slate-900/60 dark:hover:border-indigo-500/40',
    tile: 'bg-gradient-to-br from-indigo-500 to-indigo-600 text-white shadow-md shadow-indigo-600/25',
    arrow: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-300',
    glow: 'bg-indigo-500/20',
  },
];

const ShipmentTabs = ({ activeTab, onTabChange, showAllTabs = true }) => {
  if (!showAllTabs) return null;

  const card = (tab, on, width = '') => (
    <button
      key={tab.id}
      type="button"
      onClick={() => onTabChange(tab.id)}
      className={`group relative overflow-hidden flex items-center gap-3.5 px-3.5 py-3 rounded-2xl ${width}
                  border text-left shadow-sm transition duration-150 ease-out
                  hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0 active:shadow-sm
                  ${on ? `bg-gradient-to-br ${tab.on}` : tab.off}`}
    >
      <span aria-hidden="true"
        className={`pointer-events-none absolute -right-8 -top-10 w-28 h-28 rounded-full blur-2xl
                    ${on ? 'bg-white/20' : tab.glow}`} />

      <span className={`relative grid place-items-center w-11 h-11 rounded-xl flex-shrink-0
                        ${on ? 'bg-white/20 text-white' : tab.tile}`}>
        <tab.icon size={20} />
      </span>

      <span className="relative min-w-0 flex-1">
        <span className={`block text-[15px] font-bold leading-tight
                          ${on ? 'text-white' : 'text-slate-900 dark:text-white'}`}>
          {tab.label}
        </span>
        <span className={`block text-[12px] font-medium leading-tight mt-0.5
                          ${on ? 'text-white/80' : 'text-slate-500 dark:text-slate-400'}`}>
          {tab.sub}
        </span>
      </span>

      <span className={`relative grid place-items-center w-8 h-8 rounded-full flex-shrink-0 transition
                        group-hover:translate-x-0.5
                        ${on ? 'bg-white/20 text-white' : tab.arrow}`}>
        <tab.badge size={16} />
      </span>
    </button>
  );

  return (
    <div className="w-full mb-4 sm:mb-6">
      <div className="lg:hidden overflow-x-auto scrollbar-hide -mx-3 px-3">
        <div className="flex gap-2 min-w-max pb-2">
          {TABS.map((tab) => card(tab, activeTab === tab.id, 'w-[170px] flex-shrink-0'))}
        </div>
      </div>

      <div className="hidden lg:grid grid-cols-4 gap-3">
        {TABS.map((tab) => card(tab, activeTab === tab.id))}
      </div>
    </div>
  );
};

export default ShipmentTabs;
